import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { usePost, useSettings } from "../hooks/usePosts";
import { useApproval } from "../hooks/useApproval";
import { scoreColor } from "../theme/colors";
import { VideoPlayer } from "../components/media/VideoPlayer";
import { ApprovalPanel } from "../components/ApprovalPanel";
import { HashtagEditor } from "../components/HashtagEditor";
import { FeedbackPanel } from "../components/FeedbackPanel";

export function PostDetail() {
  const { id = "" } = useParams();
  const { data: post, isLoading, error } = usePost(id);
  const { data: settings } = useSettings();
  const { patch } = useApproval(id);

  // Local, controlled caption draft — saved on blur via PATCH.
  const [caption, setCaption] = useState<string>("");
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [source, setSource] = useState<string>("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (post) {
      setCaption(post.caption_feed);
      setHashtags(post.hashtags);
      setSource(post.source);
      setDirty(false);
    }
  }, [post]);

  if (isLoading) return <div className="text-brand-text-dark/60">Lade Post…</div>;
  if (error || !post)
    return (
      <div className="rounded-xl bg-red-100 p-6 text-red-900">
        Post nicht gefunden{error ? ` (${String(error)})` : ""}.
      </div>
    );

  const canPublish = Boolean(
    settings?.services.find((s) => s.name === "Cloudflare R2")?.available &&
      settings?.services.find((s) => s.name === "Instagram Graph API")?.available,
  );

  const save = () => {
    if (!dirty) return;
    patch.mutate({
      caption_feed: caption,
      hashtags,
      source,
    });
    setDirty(false);
  };

  return (
    <div className="grid grid-cols-1 gap-8 xl:grid-cols-5">
      {/* ── Media column ── */}
      <div className="xl:col-span-2">
        <Link
          to="/"
          className="mb-4 inline-block text-xs font-bold uppercase tracking-wider text-brand-text-dark/60 hover:text-brand-text-dark"
        >
          ← Dashboard
        </Link>
        {post.is_video && post.image_paths[0] ? (
          <VideoPlayer src={post.image_paths[0]} vertical lazy={false} />
        ) : post.slide_count > 1 ? (
          <div className="space-y-2">
            {post.image_paths.map((p, i) => (
              <img
                key={i}
                src={p}
                alt={`slide ${i + 1}`}
                className="w-full rounded-2xl shadow-sm ring-1 ring-black/5"
              />
            ))}
          </div>
        ) : (
          <img
            src={post.image_paths[0]}
            alt={post.topic}
            className="w-full rounded-2xl shadow-sm ring-1 ring-black/5"
          />
        )}
      </div>

      {/* ── Editor + metadata column ── */}
      <div className="space-y-4 xl:col-span-3">
        <div className="flex flex-wrap items-center gap-2">
          <Pill text={post.category} background="#0000FF" />
          {post.template ? <Pill text={post.template} background="#FF00FF" /> : null}
          {post.style ? <Pill text={post.style} background="#9B00FF" /> : null}
          {post.brand_score != null ? (
            <Pill
              text={`score ${post.brand_score}`}
              background={scoreColor(post.brand_score)}
            />
          ) : null}
          {post.slide_count > 1 && !post.is_video ? (
            <Pill text={`${post.slide_count} slides`} background="#1A1A1A" />
          ) : null}
        </div>

        <h1 className="headline text-5xl text-brand-text-dark">{post.topic}</h1>

        {post.claim ? (
          <p className="border-l-4 border-brand-magenta bg-brand-beige-deep/50 px-4 py-2 text-sm italic text-brand-text-dark/80">
            „{post.claim}"
          </p>
        ) : null}
        {post.fact ? (
          <p className="text-sm text-brand-text-dark">{post.fact}</p>
        ) : null}

        {/* Caption editor */}
        <Section title="Caption">
          <textarea
            value={caption}
            onChange={(e) => {
              setCaption(e.target.value);
              setDirty(true);
            }}
            onBlur={save}
            rows={5}
            className="w-full resize-y rounded-xl border border-black/10 bg-white px-4 py-3 text-sm focus:border-brand-blue focus:outline-none"
          />
          <div className="mt-1 flex justify-between text-[11px] text-brand-text-dark/50">
            <span>{caption.length} chars · goal 150-250</span>
            {dirty && patch.isPending ? <span>speichere…</span> : null}
            {dirty && !patch.isPending ? (
              <button
                onClick={save}
                className="rounded px-2 py-0.5 text-brand-blue hover:bg-brand-blue/10"
              >
                Save now
              </button>
            ) : null}
          </div>
        </Section>

        <Section title="Hashtags">
          <HashtagEditor
            value={hashtags}
            onChange={(next) => {
              setHashtags(next);
              setDirty(true);
              // hashtags don't trigger onBlur — save immediately
              patch.mutate({ hashtags: next });
              setDirty(false);
            }}
          />
        </Section>

        <Section title="Quelle">
          <input
            type="text"
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              setDirty(true);
            }}
            onBlur={save}
            className="w-full rounded-xl border border-black/10 bg-white px-4 py-2 text-sm focus:border-brand-blue focus:outline-none"
          />
        </Section>

        {post.reel_script ? (
          <Section title="Reel script">
            <details className="rounded-xl bg-brand-beige-deep/30 p-3 text-xs">
              <summary className="cursor-pointer font-bold">
                {((post.reel_script as { beats?: unknown[] }).beats?.length ?? 0) || 0} beats
                · narrated:{" "}
                {(post.reel_script as { has_narration?: boolean }).has_narration
                  ? "yes"
                  : "no"}
              </summary>
              <pre className="mt-2 overflow-x-auto text-[11px]">
                {JSON.stringify(post.reel_script, null, 2)}
              </pre>
            </details>
          </Section>
        ) : null}

        <ApprovalPanel
          postId={post.id}
          approval={post.approval}
          canPublish={canPublish}
          isVideo={post.is_video}
        />

        <FeedbackPanel postId={post.id} summary={post.feedback} />
      </div>
    </div>
  );
}

function Pill({ text, background }: { text: string; background: string }) {
  return (
    <span
      className="inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white"
      style={{ background }}
    >
      {text}
    </span>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-text-dark/60">
        {title}
      </div>
      {children}
    </div>
  );
}
